import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../../../components/BackButton";
import api from "../../../api/api";
import "../../Billing&Invoice/performainvoice.css";
import logo from "../../../image/logo.png";
import { termsandcondition } from "../CreatePerformaInvoice";


const numberValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatNumber = (value) =>
  numberValue(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return "NA";

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const monthNames = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  return `${String(date.getDate()).padStart(2, "0")}-${monthNames[date.getMonth()]
    }-${date.getFullYear()}`;
};

const labelValue = (value) => {
  if (value && typeof value === "object") {
    return value.label || value.value || "";
  }

  return value || "";
};

const currencySymbol = (currency) => {
  if (!currency || currency === "INR") return "₹";
  return `${currency} `;
};

const numberToWords = (num) => {
  const ones = [
    "",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ];

  const tens = [
    "",
    "",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
  ];

  const convert = (n) => {
    if (n === 0) return "";

    if (n < 20) {
      return ones[n];
    }

    if (n < 100) {
      return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    }

    if (n < 1000) {
      return (
        ones[Math.floor(n / 100)] +
        " hundred" +
        (n % 100 ? " " + convert(n % 100) : "")
      );
    }

    if (n < 100000) {
      return (
        convert(Math.floor(n / 1000)) +
        " thousand" +
        (n % 1000 ? " " + convert(n % 1000) : "")
      );
    }

    if (n < 10000000) {
      return (
        convert(Math.floor(n / 100000)) +
        " lakh" +
        (n % 100000 ? " " + convert(n % 100000) : "")
      );
    }

    return (
      convert(Math.floor(n / 10000000)) +
      " crore" +
      (n % 10000000 ? " " + convert(n % 10000000) : "")
    );
  };

  return convert(Math.floor(numberValue(num))) || "zero";
};

const ViewPerformaInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadInvoice();
  }, [id]);

  const loadInvoice = async () => {
    setLoading(true);
    setError("");

    try {
      // Read the saved invoice, not a quotation. `id` is the sale_invoice_id
      // supplied by the View icon in ListofTransactions.js, so everything shown
      // here is what is actually stored in the database.
      const response = await api.get(`/sale-invoices/${id}`);

      if (!response.data) {
        setError("Failed to load invoice.");
        return;
      }

      const inv = response.data;

      if (inv.success === false && !inv.sale_invoice_id) {
        setError(inv.error || inv.message || "Failed to load invoice.");
        return;
      }

      if (!inv || typeof inv !== "object" || !inv.sale_invoice_id) {
        setError("Invoice data not found.");
        return;
      }

      // Reshape the saved invoice + its enquiry into the flat structure the
      // mapping below already consumes, so the whole page renders from the
      // database rather than from a quotation or a stale sessionStorage draft.
      const src = inv.enquiry || {};
      const containers = Array.isArray(inv.containers) ? inv.containers : [];
      const totalCbm = containers.reduce(
        (sum, c) => sum + (numberValue(c.cbm) || 0),
        0,
      );

      const billingPartyKey = String(inv.billing_party || "")
        .trim()
        .toLowerCase();
      const BILLING_PARTY = {
        shipper: [src.first_shipper_name, src.first_shipper_address],
        consignee: [src.first_consignee_name, src.first_consignee_address],
        "notify party": [src.notify_party_1, src.notify_party_1_address],
        "notify party 1": [src.notify_party_1, src.notify_party_1_address],
        "notify party 2": [src.notify_party_2, src.notify_party_2_address],
      };
      const [bpName, bpAddress] = BILLING_PARTY[billingPartyKey] || [
        "Not provided",
        "",
      ];

      const enquiry = {
        ...src,
        inv_number: inv.inv_number || "",
        invoice_date: inv.invoice_date || "",
        job_number: inv.job_number || src.job_number || "",
        lut_number: inv.lut_number || "",
        billing_party: inv.billing_party || "",
        billing_party_name: bpName,
        billing_party_address: bpAddress,
        // No billing-party GST/state column exists, so leave these blank
        // rather than printing placeholder text on a saved invoice.
        billing_party_gst: "",
        billing_party_state: "",
        sub_total: inv.sub_total || 0,
        gst_total: inv.gst_total || 0,
        grand_total: inv.grand_total || 0,
        pdf_url: inv.pdf_url,
        sale_invoice_id: inv.sale_invoice_id,
        invoice_type: inv.invoice_type,
        charges: Array.isArray(inv.charges) ? inv.charges : [],
        containerDetails: containers,
        cbm: totalCbm ? String(totalCbm) : "",
      };

      const mappedInvoice = {
        ...enquiry,

        inv_number:
          enquiry.inv_number ||
          enquiry.invoice_number ||
          enquiry.job_number ||
          enquiry.enq_id ||
          id,

        invoice_date:
          enquiry.invoice_date ||
          enquiry.CreatedAT ||
          enquiry.creation_date ||
          enquiry.enquiry_created_at,

        company_name:
          enquiry.company_name || "GFR Logistics India Private Limited",

        company_address:
          enquiry.company_address ||
          "416, 4th floor, Vishwadeep Tower, District Centre, Janakpuri, New Delhi, India 110058",

        company_phone: enquiry.company_phone || "+91 95992 45573",

        company_email: enquiry.company_email || "info@gfrlogistics.com",

        gst_number:
          enquiry.gst_number || enquiry.company_gst || " 07AAKCG3385M1Z1",

        lut_number: enquiry.lut_number || "ADZ703250434959",

        msme_number: enquiry.msme_number || "  UDYAM-DL-11-0061053",

        billing_party_name:
          enquiry.billing_party_name ||
          enquiry.billing_party ||
          enquiry.client_name ||
          "Not provided",

        billing_party_address: enquiry.billing_party_address || "",

        billing_party_gst:
          enquiry.billing_party_gst || enquiry.billing_gst || "",

        // No billing-party state column exists; do not print a placeholder.
        billing_party_state: "",

        consignee_name:
          enquiry.consignee_name ||
          enquiry.first_consignee_name ||
          "Not provided",

        consignee_address:
          enquiry.consignee_address ||
          enquiry.first_consignee_address ||
          "",

        consignee_country: enquiry.consignee_country || "",

        shipper_name:
          enquiry.shipper_name || enquiry.first_shipper_name || "Not provided",

        shipper_address:
          enquiry.shipper_address || enquiry.first_shipper_address || "",

        notify_party_name:
          enquiry.notify_party_name ||
          enquiry.notify_party1 ||
          enquiry.notify_party_1 ||
          "Same As Consignee",

        notify_party_address:
          enquiry.notify_party_address || enquiry.notify_party_1_address || "",

        notify_party2_name:
          enquiry.notify_party2_name ||
          enquiry.notify_party2 ||
          enquiry.notify_party_2 ||
          "",

        notify_party2_address:
          enquiry.notify_party2_address || enquiry.notify_party_2_address || "",

        shipment_segment: labelValue(enquiry.shipment_segment),

        place_of_receipt:
          enquiry.place_of_receipt || enquiry.Placeof_Receipt || "NA",

        pol: enquiry.pol || "NA",

        pod: enquiry.pod || "NA",

        final_port_of_delivery:
          enquiry.final_port_of_delivery || enquiry.Placeof_Delivery || "NA",

        incoterms: enquiry.incoterms || enquiry.incoterm || "NA",

        /*
         * Freight terms
         */
        freight_terms: enquiry.freight_terms || "NA",

        commodity_name:
          enquiry.commodity_name ||
          enquiry.hs_description ||
          enquiry.general_description ||
          "NA",

        hs_code: enquiry.hs_code || "",

        hs_description: enquiry.hs_description || "",

        vessel_name: enquiry.vessel_name || "NA",

        voyage_number: enquiry.voyage_number || "NA",

        credit_days: enquiry.credit_days ?? "",

        credit_days_system: enquiry.credit_days_system ?? "",

        gross_weight: enquiry.gross_weight || "",

        cbm: enquiry.cbm || enquiry.measurement || "",

        routing: labelValue(enquiry.routing) || "NA",

        transshipment_hub: enquiry.transshipment_hub || "NA",

        reference:
          enquiry.reference ||
          enquiry.other_reference ||
          enquiry.commercial_invoice_number ||
          "",

        sub_total: enquiry.sub_total || 0,

        gst_total: enquiry.gst_total || 0,

        grand_total: enquiry.grand_total || 0,

        round_off: enquiry.round_off || 0,

        exchange_rate: enquiry.exchange_rate || 1,

        containers: Array.isArray(enquiry.containerDetails)
          ? enquiry.containerDetails
          : Array.isArray(enquiry.containers)
            ? enquiry.containers
            : [],

        charges: (() => {
          const source = Array.isArray(enquiry.quotation_charges)
            ? enquiry.quotation_charges
            : Array.isArray(enquiry.charges)
              ? enquiry.charges
              : [];

          if (source.length > 0) {
            return source.map((item, index) => {
              const currency = item.charge_currency || item.currency || "INR";

              const rate = numberValue(item.rate ?? item.amount);
              const quantity = numberValue(item.quantity) || 1;
              const amount = numberValue(
                item.amount ?? item.tax_amount ?? rate * quantity,
              );
              const exchangeRate = numberValue(item.exchange_rate) || 1;

              return {
                charge_id: item.qc_id ?? item.charge_id ?? index,
                charge_code: item.charge_code || item.charge_name || "FREIGHT",
                charge_name: item.charge_name || item.charge_code || "FREIGHT",
                quantity,
                rate,
                currency,
                charge_currency: currency,
                amount,

                amount_in_inr: numberValue(
                  item.amount_in_inr ?? amount * exchangeRate,
                ),
                gst_percentage: numberValue(item.gst_percentage),
                gst_amount: numberValue(item.gst_amount),
                total_amount: numberValue(
                  item.total_amount ?? amount + numberValue(item.gst_amount),
                ),
                gst_option: numberValue(item.gst_percentage) > 0 ? "yes" : "no",
                sac_code: item.sac_code || "NA",
                exchange_rate: exchangeRate,
              };
            });
          }

          if (numberValue(enquiry.freight_amount) > 0) {
            return [
              {
                charge_id: "freight",
                charge_code: "FREIGHT",
                charge_name: "FREIGHT",
                quantity: 1,
                rate: numberValue(enquiry.freight_amount),
                currency: enquiry.ex_currency || "INR",
                amount: numberValue(enquiry.freight_amount),
                amount_in_inr: numberValue(enquiry.freight_amount),
                gst_percentage: 0,
                sac_code: "NA",
              },
            ];
          }

          return [];
        })(),

        terms_and_conditions: Array.isArray(enquiry.terms_and_conditions)
          ? enquiry.terms_and_conditions
          : null,

        bank_account_name: "GFR Logistics India Private Limited",

        bank_name: "Yes Bank (India)",

        bank_account_number: "10576370000052",

        bank_ifsc: "YESB0000107",

        bank_swift: "YESBINBB",

        bank_address: "GF, SCO No.3, SECTOR 20 D, Chandigarh-160020",

        registered_address:
          "Office No. 416, 4th Floor, Vishwa Deep Tower, District Centre, Janakpuri, New Delhi, India - 110058",
      };

      console.log("Mapped invoice:", mappedInvoice);

      setInvoice(mappedInvoice);
    } catch (err) {
      console.error("Failed to load performa invoice:", err);

      console.error("API error response:", err?.response?.data);

      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to load invoice.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Same behaviour as handleDownloadInvoice in ListofTransactions.js: open the
  // PDF that was already generated and uploaded to Azure. Nothing is re-rendered
  // here, so what the user downloads always matches the saved invoice.
  // Streams the PDF that was already generated and uploaded to Azure, the same
  // way ListofBLAdmin.js downloads a BL draft. Nothing is re-rendered here, so
  // what the user downloads always matches the saved invoice.
  const handleDownloadPdf = async () => {
    if (!invoice?.pdf_url || !invoice?.sale_invoice_id) {
      alert(
        "PDF not available yet. Please generate the performa invoice first.",
      );
      return;
    }

    try {
      const response = await api.get(
        `/download-document/performa/${invoice.sale_invoice_id}`,
        { responseType: "blob" },
      );

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Performa_Invoice_${invoice.inv_number || invoice.sale_invoice_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download the invoice PDF. Please try again.");
    }
  };

  if (loading) {
    return (
      <>
        <BackButton />

        <div className="container py-5 text-center">
          <div className="spinner-border" role="status" />

          <div className="mt-3">Loading performa invoice...</div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <BackButton />

        <div className="container py-4">
          <div className="alert alert-danger">
            <strong>Failed to load invoice</strong>

            <div className="mt-2">{error}</div>

            <div className="mt-2">Invoice ID: {id}</div>
          </div>

          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </>
    );
  }

  if (!invoice) {
    return (
      <>
        <BackButton />

        <div className="container py-4">
          <div className="alert alert-warning">Invoice not found.</div>
        </div>
      </>
    );
  }

  const charges = Array.isArray(invoice.charges) ? invoice.charges : [];

  const containers = Array.isArray(invoice.containers)
    ? invoice.containers
    : [];

  const subTotal = numberValue(invoice.sub_total);

  const gstTotal = numberValue(invoice.gst_total);

  const grandTotal = numberValue(invoice.grand_total);

  const roundOff = numberValue(invoice.round_off);

  const shipmentSegment = labelValue(invoice.shipment_segment);

  const placeOfSupply = (invoice.job_number || "").slice(3, 5);

  const isInterState = placeOfSupply && placeOfSupply !== "07";

  const billingParty = invoice.billing_party_name || "Not provided";

  const consigneeName = invoice.consignee_name || "Not provided";

  const shipperName = invoice.shipper_name || "Not provided";

  const notifyName = invoice.notify_party_name || "Same As Consignee";

  const notifyParty2 =
    invoice.notify_party2_name ||
    invoice.notify_party2 ||
    invoice.notify_party2_address ||
    "";

  // Only render the Notify Party 2 column when it was chosen as the billing
  // party. The enquiry may always carry notify_party_2 data, so presence alone
  // would print the column on every invoice.
  const hasNotifyParty2 =
    String(labelValue(invoice.billing_party)).trim().toLowerCase() ===
      "notify party 2" && Boolean(notifyParty2);

  const containerText = containers
    .map((container) =>
      [container.container_number, container.container_type]
        .filter(Boolean)
        .join(" / "),
    )
    .filter(Boolean)
    .join(", ");

  const grossWeight = invoice.gross_weight
    ? `${numberValue(invoice.gross_weight)} KGS`
    : "NA";

  const amountWords = `INR ${numberToWords(grandTotal).toUpperCase()} ONLY`;

  return (
    <>
      <BackButton />

      <div className="performa-invoice-page">
        {/* ACTION BUTTONS */}

       
         

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDownloadPdf}
                disabled={!invoice?.pdf_url}
                title={
                  invoice?.pdf_url
                    ? "Download the generated PDF"
                    : "PDF not available yet"
                }
              >
                Download
              </button>
            </div>
        
       

        {/* ================= INVOICE ================= */}

        <div id="invoice" className="invoice">
          {/* ================= HEADER ================= */}

          <table className="table table-bordered invoice-table custom-invoice-table mb-0 table-layout-fixed">
            <tbody>
              <tr className="b-0">
                <td
                  rowSpan="9"
                  colSpan="3"
                  className="text-center align-top b-0 p-0"
                  style={{ width: "253px" }}
                >
                  <div className="d-flex flex-column gap-2 mt-2">
                    <img
                      src={logo}
                      alt="logo"
                      className="px-3 mx-auto"
                      style={{
                        maxHeight: "90px",
                        maxWidth: "200px",
                        objectFit: "contain",
                        alignItems: "center",
                      }}
                    />

                    <div
                      className="bt d-flex"
                      style={{
                        height: "150px",
                      }}
                    />
                  </div>
                </td>

                <td colSpan="5" className="text-center fw-bold fs-6 b-0 bl">
                  {invoice.company_name ||
                    "GFR Logistics India Private Limited"}
                </td>
              </tr>

              <tr className="b-0">
                <td
                  colSpan="1"
                  className="br-0 bb-0"
                  style={{
                    width: "18%",
                    fontWeight: "bold",
                  }}
                >
                  Corporate Address :
                </td>

                <td
                  colSpan="4"
                  className="br-0 bb-0"
                  style={{
                    width: "20%",
                  }}
                >
                  {invoice.company_address ||
                    "416, 4th floor, Vishwadeep Tower, District Centre, Janakpuri, New Delhi, India 110058"}
                </td>
              </tr>

              <tr className="b-0">
                <td
                  colSpan="1
                  "
                  className="br-0 bb-0"
                  style={{
                    fontWeight: "bold",
                    width: "18%",
                  }}
                >
                  LUT Number :
                </td>

                <td
                  colSpan="4"
                  className="br-0 bb-0"
                  style={{
                    width: "20%",
                  }}
                >
                  {invoice.lut_number || "ADZ703250434959"}
                </td>
              </tr>

              <tr className="b-0">
                <td
                  colSpan="1"
                  className="br-0 bb-0"
                  style={{
                    fontWeight: "bold",
                    width: "18%",
                  }}
                >
                  Telephone :
                </td>

                <td colSpan="4" className="br-0 bb-0">
                  {invoice.company_phone || "+91 95992 45573"}
                </td>
              </tr>

              <tr className="b-0">
                <td
                  colSpan="1"
                  className="br-0 bb-0"
                  style={{
                    fontWeight: "bold",
                    width: "18%",
                  }}
                >
                  General Enquiries :
                </td>

                <td colSpan="4" className="br-0 bb-0">
                  {invoice.company_email || "info@gfrlogistics.com"}
                </td>
              </tr>

              <tr className="b-0">
                <td
                  colSpan="1"
                  className="br-0 bb-0"
                  style={{
                    fontWeight: "bold",
                    width: "18%",
                  }}
                >
                  INVOICE NUMBER :
                </td>

                <td colSpan="4" className="br-0 bb-0">
                  {invoice.inv_number || id}
                </td>
              </tr>

              <tr className="b-0">
                <td
                  colSpan="1"
                  className="br-0 bb-0"
                  style={{
                    fontWeight: "bold",
                    width: "18%",
                  }}
                >
                  Invoice Date :
                </td>

                <td colSpan="4" className="br-0 bb-0">
                  {formatDate(invoice.invoice_date)}
                </td>
              </tr>

              <tr className="b-0">
                <td
                  colSpan="1"
                  className="br-0 bb-0"
                  style={{
                    fontWeight: "bold",
                    width: "18%",
                  }}
                >
                  GST Number :
                </td>

                <td colSpan="4" className="br-0 bb-0">
                  {invoice.gst_number || " 07AAKCG3385M1Z1"}
                </td>
              </tr>

              <tr className="b-0">
                <td
                  colSpan="1"
                  className="br-0 bb-0"
                  style={{
                    fontWeight: "bold",
                    width: "18%",
                  }}
                >
                  MSME Number :
                </td>

                <td colSpan="4" className="br-0 bb-0">
                  {invoice.msme_number || "  UDYAM-DL-11-0061053"}
                </td>
              </tr>

              <tr className="b-0">
                <td
                  className="text-center br-0 bb-0 bl-0"
                  colSpan="8"
                  style={{
                    height: "33px",
                    verticalAlign: "middle",
                  }}
                >
                  <b className="mnhnd">TAX INVOICE</b>
                </td>
              </tr>

              {/* BILLING / CONSIGNEE */}

              <tr className="b-0">
                <td colSpan="4" className="bl-0 bb-0">
                  <b>BILLING PARTY</b>
                </td>

                <td colSpan="4" className="br-0 bl-0 bb-0">
                  <b>CONSIGNEE</b>
                </td>
              </tr>

              <tr className="b-0">
                <td colSpan="4" className="bl-0 bb-0">
                  {billingParty}
                  <br />
                  {invoice.billing_party_address}
                </td>

                <td colSpan="4" className="br-0 bl-0 bb-0">
                  {consigneeName}
                  <br />

                  {invoice.consignee_address}

                  <br />

                  {invoice.consignee_country || ""}
                </td>
              </tr>

              {/* SHIPPER / NOTIFY */}

              <tr className="b-0">
                <td colSpan="4" className="bl-0 bb-0">
                  <b>SHIPPER</b>
                </td>

                <td colSpan={hasNotifyParty2 ? 2 : 4} className="bl-0 bb-0">
                  <b>NOTIFY PARTY</b>
                </td>

                {hasNotifyParty2 && (
                  <td colSpan="2" className="br-0 bl-0 bb-0">
                    <b>NOTIFY PARTY 2</b>
                  </td>
                )}
              </tr>

              <tr className="b-0">
                <td colSpan="4" className="bl-0 bb-0">
                  {shipperName}
                  <br />

                  {invoice.shipper_address}
                </td>

                <td
                  colSpan={hasNotifyParty2 ? 2 : 4}
                  className="bl-0 bb-0 align-top"
                >
                  {notifyName}

                  {invoice.notify_party_address && (
                    <>
                      <br />
                      {invoice.notify_party_address}
                    </>
                  )}
                </td>

                {hasNotifyParty2 && (
                  <td colSpan="2" className="bl-0 bb-0 br-0 align-top">
                    {notifyParty2}

                    {invoice.notify_party2_address && (
                      <>
                        <br />
                        {invoice.notify_party2_address}
                      </>
                    )}
                  </td>
                )}
              </tr>

              {/* JOB */}

              <tr className="b-0">
                <td colSpan="2" className="bl-0 bb-0">
                  <strong>JOB NUMBER</strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0">
                  {invoice.job_number || "NA"}
                </td>

                <td colSpan="2" className="bl-0 bb-0">
                  <strong>HBL Number</strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0 br-0">
                  {invoice.bl_number || "NA"}
                </td>
              </tr>

              {/* PORTS */}

              <tr className="b-0">
                <td colSpan="2" className="bl-0 bb-0">
                  <strong>PLACE OF RECEIPT</strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0">
                  {invoice.place_of_receipt || "NA"}
                </td>

                <td colSpan="2" className="bl-0 bb-0">
                  <strong>PORT OF LOADING</strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0">
                  {invoice.pol || "NA"}
                </td>
              </tr>

              <tr className="b-0">
                <td colSpan="2" className="bl-0 bb-0">
                  <strong>PORT OF DISCHARGE</strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0">
                  {invoice.pod || "NA"}
                </td>

                <td colSpan="2" className="bl-0 bb-0">
                  <strong>FINAL PORT OF DELIVERY</strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0 br-0">
                  {invoice.final_port_of_delivery || "NA"}
                </td>
              </tr>

              {/* SEGMENT */}

              <tr className="b-0">
                <td colSpan="2" className="bl-0 bb-0">
                  <strong>SHIPMENT SEGMENT</strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0">
                  {shipmentSegment || "NA"}
                </td>

                <td colSpan="2" className="bl-0 bb-0">
                  <strong>SHIPMENT INCOTERMS</strong>
                </td>

                <td colSpan="2" className="bl-0 bb-0">
                  {labelValue(invoice.incoterms) || "NA"}
                </td>
              </tr>

              {/* COMMODITY */}

              <tr className="b-0">
                <td colSpan="2" className="bl-0 bb-0">
                  <strong>COMMODITY NAME</strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0">
                  {invoice.commodity_name || "NA"}
                </td>

                <td colSpan="2" className="bl-0 bb-0">
                  <strong>VESSEL NAME</strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0">
                  {invoice.vessel_name || "NA"}
                </td>
              </tr>

              {/* VOYAGE */}

              <tr className="b-0">
                <td colSpan="2" className="bl-0 bb-0">
                  <strong>VOYAGE NUMBER</strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0">
                  {invoice.voyage_number || "NA"}
                </td>

                <td colSpan="2" className="bl-0 bb-0">
                  <strong>FREIGHT TERMS</strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0">
                  {labelValue(invoice.freight_terms) || "NA"}
                </td>
              </tr>

              {/* CREDIT DAYS */}

              <tr className="b-0">
                <td colSpan="2" className="bl-0 bb-0">
                  <strong>CREDIT DAY ACTUAL </strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0">
                  {invoice.credit_days ?? ""}
                </td>

                <td colSpan="2" className="bl-0 bb-0">
                  <strong>CREDIT AS PER SYSTEM</strong>
                </td>

                <td colSpan="2" className="bb-0 bl-0">
                  {invoice.credit_days_system ?? ""}
                </td>
              </tr>

              {/* WEIGHT / CNTR */}

              <tr className="b-0">
                {["FE", "FI"].includes(shipmentSegment) && (
                  <>
                    <td colSpan="2" className="bl-0 bb-0">
                      <strong>GROSS WEIGHT</strong>
                    </td>

                    <td colSpan="2" className="bb-0 bl-0">
                      {grossWeight}
                    </td>
                  </>
                )}

                {["LE", "LI"].includes(shipmentSegment) && (
                  <>
                    <td colSpan="2" className="bl-0 bb-0">
                      <strong>CBM</strong>
                    </td>

                    <td colSpan="2" className="bb-0 bl-0">
                      {invoice.cbm || "NA"}
                    </td>
                  </>
                )}

                {["FE", "FI"].includes(shipmentSegment) && (
                  <>
                    <td colSpan="2" className="bl-0 bb-0">
                      <strong>CONTAINER TYPE</strong>
                    </td>

                    <td colSpan="2" className="bb-0 bl-0 br-0">
                      {containerText || "NA"}
                    </td>
                  </>
                )}
              </tr>

              {/* ROUTING */}

              <tr className="b-0">
                <td colSpan="2" className="bl-0 bb-0 br-0 text-start">
                  <strong>ROUTING</strong>
                </td>

                <td
                  colSpan={
                    invoice.routing === "Both" || invoice.routing === "Indirect"
                      ? 2
                      : 6
                  }
                  className="br-0 bb-0 text-start"
                >
                  {labelValue(invoice.routing) || "NA"}
                </td>

                {(invoice.routing === "Both" ||
                  invoice.routing === "Indirect") && (
                    <>
                      <td colSpan="2" className="bb-0 br-0 text-start">
                        <strong>TRANSSHIPMENT HUB</strong>
                      </td>

                      <td colSpan="2" className="bb-0 br-0 text-start">
                        {invoice.transshipment_hub || "NA"}
                      </td>
                    </>
                  )}
              </tr>

              {/* REFERENCE */}

              <tr className="b-0">
                <td colSpan="8" className="bl-0 bb-0 br-0 text-start">
                  <strong>REFRENCE :</strong> {invoice.reference || ""}
                </td>
              </tr>
            </tbody>
          </table>

          {/* ================= CHARGES ================= */}

          <div id="charges-block" className="charges-block">
            <table className="table table-bordered invoice-table custom-invoice-table mb-0 table-layout-fixed bt-0">
              <tbody>
                <tr className="b-0">
                  <th className="fw-bold bb-0 bl-0" style={{ width: "20%" }}>CHARGE CODE</th>
                    <th className="fw-bold bb-0 bl-0 br-0">SAC</th>

                  <th className="fw-bold bb-0 ">QTY</th>

                  <th className="fw-bold bb-0 ">RATE</th>

                  <th className="fw-bold bb-0 ">CURR.</th>

                  <th className="fw-bold bb-0 ">AMOUNT</th>

                  <th className="fw-bold bb-0">AMOUNT INR</th>

                  <th className="fw-bold bb-0 ">GST</th>

                
                </tr>

                {charges.length > 0 ? (
                  charges.map((charge, index) => {

                      const sac = charge.sac_code || "NA";
                    const currency =
                      charge.currency || charge.charge_currency || "INR";

                    const amount = numberValue(charge.amount);

                    const amountInInr = numberValue(
                      charge.amount_in_inr ?? charge.amount,
                    );

                  

                    const gstPercentage = numberValue(charge.gst_percentage);

                    const gstLabel =
                      gstPercentage > 0 ? `${gstPercentage}%` : "No";

                    const chargeLabel =
                      charge.charge_code || charge.charge_name || "FREIGHT";

                    return (
                      <tr
                        className="b-0"
                        key={charge.charge_id || charge.id || index}
                      >
                        <td className="bb-0 bl-0">{labelValue(chargeLabel)}</td>
<td className="bb-0 bl-0 br-0">{sac}</td>
                        <td className="bb-0 ">
                          {formatNumber(charge.quantity || 1)}
                        </td>

                        <td className="bb-0 ">
                          {formatNumber(charge.rate || amount)}
                        </td>

                        <td className="bb-0 ">{currency}</td>

                        <td className="bb-0 ">
                          {currencySymbol(currency)}
                          {formatNumber(amount)}
                        </td>

                        <td className="bb-0 ">
                          {formatNumber(amountInInr)}
                        </td>

                        <td className="bb-0 ">{gstLabel}</td>

                        
                      </tr>
                    );
                  })
                ) : (
                  <tr className="b-0">
                    <td colSpan="8" className="text-center bb-0 bl-0 br-0">
                      No charges available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ================= TOTALS ================= */}

          <div className="pdf-keep-together">

          </div>

          <table className="table invoice-table custom-invoice-table table-layout-fixed mb-0 border-0">
            <tbody>
              <tr>
                {/* LEFT */}
                <td colSpan="5" className="p-0 b-0">
                  <table className="table invoice-table custom-invoice-table table-layout-fixed mb-0 bt-0">
                    <tbody>
                      <tr>
                        <td colSpan="4" className="fw-bold text-center bt-0">SUB TOTAL</td>
                        <td className="fw-bold text-center bt-0">{formatNumber(subTotal)}</td>
                        <td colSpan="3" className="fw-bold text-center bt-0">EXCHANGE RATE</td>
                      </tr>

                      <tr>
                        <td colSpan="4" className="fw-bold text-center">
                          {isInterState ? "IGST" : "CGST / SGST"}
                        </td>
                        <td className="fw-bold text-center">{formatNumber(gstTotal)}</td>
                        <td colSpan="3" className="text-center">
                          1$ = ₹ {formatNumber(invoice.exchange_rate)}
                        </td>
                      </tr>

                      <tr>
                        <td colSpan="4" className="fw-bold text-center">ROUND OFF</td>
                        <td className="fw-bold text-center">({formatNumber(roundOff)})</td>

                        {/* stamp cell spans the remaining 3 rows */}
                        <td
                          colSpan="3"
                          rowSpan="3"
                          className="text-center"
                          style={{ verticalAlign: "bottom" }}
                        >
                          {invoice.company_stamp && (
                            <img src={invoice.company_stamp} alt="Stamp" style={{ width: "120px" }} />
                          )}
                        </td>
                      </tr>

                      <tr>
                        <td colSpan="4" className="fw-bold text-center">GRAND TOTAL</td>
                        <td className="fw-bold text-center">{formatNumber(grandTotal)}</td>
                      </tr>

                      <tr>
                        <td colSpan="5" className="fw-bold text-start">{amountWords}</td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>

          <table className="table table-bordered invoice-table custom-invoice-table bt-0">
            <tbody>
              <tr>
                {/* TERMS */}

                {/* TERMS */}
                <td width="50%" style={{ verticalAlign: "top" }} className="px-0 bt-0">
                  <div className="section-title">TERMS & CONDITIONS</div>

                  <ol style={{ paddingLeft: 18, marginTop: 8, fontSize: "9px" }}>
                    {termsandcondition.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ol>
                </td>

                {/* BANK */}
                <td width="50%" style={{ verticalAlign: "top" }} className="px-0 bt-0">
                  <div className="section-title">Bank Details</div>

                  <div className="bank-grid">
                    <span className="lbl">ACCOUNT NAME</span>
                    <span>:</span>
                    <span>GFR Logistics India Private Limited</span>

                    <span className="lbl">BANK NAME</span>
                    <span>:</span>
                    <span>Yes Bank (India)</span>

                    <span className="lbl">ACCOUNT NUMBER</span>
                    <span>:</span>
                    <span>10576370000052</span>

                    <span className="lbl">IFSC</span>
                    <span>:</span>
                    <span>YESB0000107</span>

                    <span className="lbl">SWIFT</span>
                    <span>:</span>
                    <span>YESBINBB</span>

                    <span className="lbl">BANK ADDRESS</span>
                    <span>:</span>
                    <span>GF, SCO No.3, Sector 20-D, Chandigarh - 160020</span>
                  </div>
                </td>
              </tr>

              <tr>
                <td colSpan="2">
                  <b>Registered Address :</b>

                  {invoice.registered_address}
                </td>
              </tr>
            </tbody>
          </table>

          <table
            id="computer-generated-note"
            className="table table-bordered invoice-table custom-invoice-table bt-0 bb-0"
          >
            <tbody className="lstftr">
              <tr className="b-0">
                <td colSpan="8" className="text-center br-0 bl-0 bb-0 bt-0">
                  <b>This is a computer generated invoice</b>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </>
  );
};

export default ViewPerformaInvoice;